import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import { X } from "lucide-react";

export function AutoTextarea({ value, onChange, className = "", ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [value]);
  const onKeyDown = (e) => {
    const touch = window.matchMedia?.("(pointer: coarse)").matches;
    if (e.key === "Enter" && !e.shiftKey && !touch) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); }
  };
  return <textarea ref={ref} rows={1} value={value} onChange={onChange} onKeyDown={onKeyDown} className={`resize-none leading-5 py-2 max-h-40 overflow-y-auto custom-scrollbar ${className}`} {...rest} />;
}

export function Lightbox({ src, alt = "", onClose }) {
  useEffect(() => {
    if (!src) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [src, onClose]);
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in" onClick={onClose} data-testid="lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="lightbox-close"><X className="w-6 h-6" /></button>
      <img src={src} alt={alt} onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" data-testid="lightbox-image" />
    </div>
  );
}

export function Logo({ size = "text-2xl", onClick }) {
  return (
    <Link to="/" onClick={onClick} className="flex items-center gap-2 select-none" data-testid="brand-logo">
      <img src="/icon-192.png" alt="PokeDrop" className="w-8 h-8 rounded-lg gold-glow" />
      <span className={`font-display font-black tracking-tight ${size}`}>
        <span className="text-white">Poke</span><span className="gold-text">Drop</span>
      </span>
    </Link>
  );
}

const STATUS = {
  active: { label: "ACTIVE", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10", beacon: true },
  scheduled: { label: "SCHEDULED", cls: "text-sky-400 border-sky-400/40 bg-sky-400/10" },
  drawing: { label: "DRAWING", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10", beacon: true },
  full: { label: "FULL", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  drawn: { label: "DRAWN", cls: "text-slate-400 border-slate-500/40 bg-slate-500/10" },
  cancelled: { label: "CANCELLED", cls: "text-red-400 border-red-400/40 bg-red-400/10" },
};

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.active;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-mono font-bold tracking-widest ${s.cls}`} data-testid={`status-${status}`}>
      {s.beacon && <span className="w-1.5 h-1.5 rounded-full bg-current beacon" />}
      {s.label}
    </span>
  );
}

export function Loader({ label = "Loading" }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400">
      <div className="w-10 h-10 rounded-full border-2 border-gold/20 border-t-gold animate-spin" />
      <p className="mt-4 font-mono text-xs tracking-widest uppercase">{label}</p>
    </div>
  );
}

export function GradeBadge({ grade }) {
  if (!grade) return null;
  return (
    <span className="px-2 py-0.5 rounded-md gold-gradient text-obsidian text-[10px] font-black tracking-wide" data-testid="grade-badge">
      {grade}
    </span>
  );
}
